import { useState, useEffect, useCallback } from 'react';
import { useCompanyFilter } from '../contexts/CompanyFilterContext';
import { getTransactions } from '../apis/reconAuditApi';

const thStyle = {
  textAlign: 'left', padding: '10px 12px', fontSize: '12px', fontWeight: 700,
  color: '#0039A6', textTransform: 'uppercase', letterSpacing: '0.5px',
  borderBottom: '2px solid #0098DB', backgroundColor: '#f8f9fa', whiteSpace: 'nowrap',
};
const tdStyle = {
  padding: '9px 12px', fontSize: '13px', color: '#333',
  borderBottom: '1px solid #eee', verticalAlign: 'top',
};

export function TransactionsExplorer() {
  const { selectedCompany } = useCompanyFilter();
  const [startDate, setStartDate] = useState('2020-01-01');
  const [endDate, setEndDate] = useState(() => {
    const d = new Date();
    return d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0');
  });
  const [orderNumber, setOrderNumber] = useState('');
  const [sotNumber, setSotNumber] = useState('');
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(25);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchTransactions = useCallback(async () => {
    setLoading(true);
    setError(null);
    const params = {
      page, page_size: pageSize, sort_by: 'updated_on', sort_dir: 'desc',
      company: selectedCompany?.trim() || undefined,
    };
    if (orderNumber.trim()) params.order_num = orderNumber.trim();
    if (sotNumber.trim()) params.item_number = sotNumber.trim();
    if (startDate?.trim()) params.from_date = startDate.trim().slice(0, 10);
    if (endDate?.trim()) params.to_date = endDate.trim().slice(0, 10);
    try {
      const res = await getTransactions(params);
      setRows(res.rows);
      setTotal(res.total);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load transactions');
      setRows([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, selectedCompany, orderNumber, sotNumber, startDate, endDate]);

  useEffect(() => { fetchTransactions(); }, [fetchTransactions]);

  const formatDate = (s) =>
    s ? (s.slice(0, 10) === s ? s : s.slice(0, 19).replace('T', ' ')) : '—';

  return (
    <div>
      {/* Search Filters */}
      <article className="card">
        <header>
          <i className="fa fa-search"></i>
          <label>Search Filters</label>
        </header>
        <div className="body">
          <div className="group">
            <div className="span3">
              <div className="field">
                <label>Start Date</label>
                <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
              </div>
            </div>
            <div className="span3">
              <div className="field">
                <label>End Date</label>
                <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} min="2020-06-01" />
              </div>
            </div>
            <div className="span3">
              <div className="field">
                <label>Order Number</label>
                <input type="text" value={orderNumber} onChange={(e) => setOrderNumber(e.target.value)} placeholder="Enter order number" />
              </div>
            </div>
            <div className="span3">
              <div className="field">
                <label>Item / SOT Number</label>
                <input type="text" value={sotNumber} onChange={(e) => setSotNumber(e.target.value)} placeholder="Enter item number" />
              </div>
            </div>
          </div>
          <div className="group">
            <div className="span12">
              <button className="btn-blue right" onClick={() => { setPage(1); }}>
                <i className="fa fa-search"></i> Search
              </button>
            </div>
          </div>
        </div>
      </article>

      {error && (
        <div style={{ padding: '12px 16px', backgroundColor: '#fef2f2', border: '1px solid #fecaca', borderRadius: '4px', color: '#991b1b', fontSize: '13px', marginBottom: '15px' }}>
          {error}
        </div>
      )}

      {/* Results Table */}
      <article className="card">
        <header>
          <i className="fa fa-table"></i>
          <label>Search Results ({total})</label>
        </header>
        <div className="body" style={{ padding: 0 }}>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  {['Company', 'Recon ID', 'Order #', 'Sub Order', 'Item #', 'Tracking #',
                    'HIB AOC Status', 'Veeva AOC Status', 'Recon Status',
                    'Veeva Ord Identifier', 'Veeva AOC Identifier',
                    'Comments', 'PCR #', 'Updated By', 'Updated On', 'Active'].map((h) => (
                    <th key={h} style={thStyle}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={16} style={{ ...tdStyle, textAlign: 'center', padding: '40px', color: '#999' }}>Loading…</td></tr>
                ) : rows.length === 0 ? (
                  <tr><td colSpan={16} style={{ ...tdStyle, textAlign: 'center', padding: '40px', color: '#999' }}>No records found.</td></tr>
                ) : (
                  rows.map((row) => (
                    <tr key={row.recon_id} onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#f0f7ff'; }} onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = ''; }}>
                      <td style={tdStyle}>{row.company ?? '—'}</td>
                      <td style={tdStyle}>{row.recon_id ?? '—'}</td>
                      <td style={tdStyle}>{row.order_num ?? '—'}</td>
                      <td style={tdStyle}>{row.sub_order_num ?? '—'}</td>
                      <td style={tdStyle}>{row.item_number ?? '—'}</td>
                      <td style={{ ...tdStyle, maxWidth: '140px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={row.tracking_num ?? ''}>{row.tracking_num ?? '—'}</td>
                      <td style={tdStyle}>{row.hib_aoc_status ?? '—'}</td>
                      <td style={tdStyle}>{row.veeva_aoc_status ?? '—'}</td>
                      <td style={tdStyle}>{row.recon_status ?? '—'}</td>
                      <td style={{ ...tdStyle, maxWidth: '120px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={row.veeva_ord_identifier ?? ''}>{row.veeva_ord_identifier ?? '—'}</td>
                      <td style={{ ...tdStyle, maxWidth: '120px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={row.veeva_aoc_identifer ?? ''}>{row.veeva_aoc_identifer ?? '—'}</td>
                      <td style={{ ...tdStyle, maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={row.comments ?? ''}>{row.comments ?? '—'}</td>
                      <td style={tdStyle}>{row.pcr_num ?? '—'}</td>
                      <td style={tdStyle}>{row.updated_by ?? '—'}</td>
                      <td style={{ ...tdStyle, color: '#666' }}>{formatDate(row.updated_on)}</td>
                      <td style={tdStyle}>{row.active_flag === 1 ? 'Yes' : 'No'}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          {!loading && total > 0 && (
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 16px', borderTop: '1px solid #eee', fontSize: '13px', color: '#666' }}>
              <span>Showing {(page - 1) * pageSize + 1}–{Math.min(page * pageSize, total)} of {total}</span>
              <div style={{ display: 'flex', gap: '8px' }}>
                <button className="btn-blue" disabled={page <= 1} onClick={() => setPage((p) => p - 1)} style={{ padding: '4px 14px', height: '30px', fontSize: '12px', opacity: page <= 1 ? 0.5 : 1 }}>Previous</button>
                <button className="btn-blue" disabled={page * pageSize >= total} onClick={() => setPage((p) => p + 1)} style={{ padding: '4px 14px', height: '30px', fontSize: '12px', opacity: page * pageSize >= total ? 0.5 : 1 }}>Next</button>
              </div>
            </div>
          )}
        </div>
      </article>
    </div>
  );
}
